import { useId } from "react";
import { Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import { bucketSpans, stepHexOverWhite } from "../../../util/reportColors";

export default function RankPercentileLegend({
  rankPopulation = 30,
}: {
  rankPopulation?: number;
}) {
  // The key must be SVG, not CSS backgrounds — Chrome's print-to-PDF
  // rasterizes backgrounds in repeating table heads unreliably. An SVG fill
  // prints as vector. The clip id must be unique per instance: the print
  // header repeats this legend on every page.
  const clipId = useId();
  const endLabelSx = {
    fontSize: "0.69rem",
    lineHeight: 1,
    color: "text.secondary",
    fontFamily: "Roboto Condensed",
  };
  const axisLabelSx = {
    fontSize: "0.632rem",
    lineHeight: 1,
    color: "text.disabled",
    fontFamily: "Roboto Condensed",
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
  };

  return (
    <Stack
      className="RankPercentileLegend"
      direction="row"
      spacing={1.5}
      alignItems="center"
    >
      <Stack spacing={0.3} sx={{ width: 150 }}>
      {/* Rank axis (season reports) */}
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography component="div" sx={endLabelSx}>
          {rankPopulation}th
        </Typography>
        <Typography component="div" sx={axisLabelSx}>
          Rank
        </Typography>
        <Typography component="div" sx={endLabelSx}>
          1st
        </Typography>
      </Stack>

      <svg width="100%" height={8} style={{ display: "block" }}>
        <defs>
          <clipPath id={clipId}>
            <rect x="0.5" y="0.5" width="99%" height={7} rx={3.5} />
          </clipPath>
        </defs>
        {/* One block per bucket at its share of the rank axis, so the key
            matches the chips step for step */}
        <g clipPath={`url(#${clipId})`}>
          {bucketSpans().map(({ from, to, step }) => (
            <rect
              key={from}
              x={`${(from / 30) * 99}%`}
              y="0.5"
              width={`${((to - from) / 30) * 99 + 0.1}%`}
              height={7}
              fill={stepHexOverWhite(step)}
            />
          ))}
        </g>
        <rect
          x="0.5"
          y="0.5"
          width="99%"
          height={7}
          rx={3.5}
          fill="none"
          stroke="rgba(0, 0, 0, 0.12)"
          strokeWidth={1}
        />
      </svg>

      {/* Percentile axis (game reports) */}
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography component="div" sx={endLabelSx}>
          0
        </Typography>
        <Typography component="div" sx={axisLabelSx}>
          Percentile
        </Typography>
        <Typography component="div" sx={endLabelSx}>
          100
        </Typography>
      </Stack>
      </Stack>
    </Stack>
  );
}
